import { useMemo } from 'react';
import { EscalationType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { ThreadItem } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { QueryOptions } from 'react-query';
import { useMessagingAIRCustomizationFlag } from '@frontend/shared';
import { isCallRecordItem, isSmsMessageItem, isAiActivityItem } from '../utils/thread-items-utils';
import { useGetUnifiedThreadQuery as useGetUnifiedThreadV4Query } from '../v4/queries';

type UseGetThreadQueryDataProps = {
  threadId: string;
  locationId: string;
  focusItemId?: string;
  focusItemCreatedAt?: string;
  enabled?: boolean;
  retryLimit?: QueryOptions['retry'];
  cacheTime?: number;
  staleTime?: number;
  /** When true, include AI activity items regardless of the fusion customization flag. */
  overrideFusionFlag?: boolean;
};

export const useGetThreadQueryData = ({
  threadId,
  locationId,
  focusItemId,
  focusItemCreatedAt,
  enabled,
  retryLimit,
  cacheTime,
  staleTime,
  overrideFusionFlag,
}: UseGetThreadQueryDataProps) => {
  const { isActive: isMessagingAIRActive } = useMessagingAIRCustomizationFlag([locationId]);
  const showAiActivity = overrideFusionFlag || isMessagingAIRActive;

  const v4Request = useMemo(
    () => ({
      threadId,
      locationId,
      pageSize: 50,
      filter: {
        focusThreadItem: focusItemId,
        focusCreatedAt: focusItemCreatedAt,
        showDeleted: true,
      },
    }),
    [threadId, locationId, focusItemId, focusItemCreatedAt]
  );

  const getThreadQuery = useGetUnifiedThreadV4Query({
    request: v4Request,
    options: {
      retry: retryLimit !== undefined ? retryLimit : 1,
      enabled: enabled !== undefined ? enabled : true,
      ...(cacheTime !== undefined && { cacheTime }),
      ...(staleTime !== undefined && { staleTime }),
    },
  });

  const threadItems: ThreadItem[] = useMemo(() => {
    return (
      getThreadQuery.data?.pages
        .flatMap((page) =>
          (page.thread.items ?? []).filter((item) => {
            // Filter out AI Trainer → Office escalations (HITL activity, not relevant for inbox)
            const isAiActivity = isAiActivityItem(item) && !!item.aiActivity;
            if (isAiActivity && item.aiActivity.escalationType === EscalationType.ESCALATION_TYPE_INTERNAL) {
              return false;
            }

            return (
              isSmsMessageItem(item) ||
              (isCallRecordItem(item) && !!item.callRecord) ||
              (showAiActivity && isAiActivity)
            );
          })
        )
        .reverse() ?? []
    );
  }, [showAiActivity, getThreadQuery.data]);

  return {
    getThreadQuery,
    threadItems,
  };
};
